import React, { forwardRef, useEffect, useState } from 'react';
import Cookies from 'js-cookie';
import { requestAvalList } from '../../../services/requests';
import { requestInputsChoices } from '../../../services/newNotesRequest';
import NewNotificacaoPDF from '../notificacao/newNotificacaoPDF';
import NewClassificacaoPDF from '../classificacao/newClassificacaoPDF';
import NewInvestigacaoPDF from '../investigacao/newInvestigacaoPDF';

const emptyAvaliacaoData = {
	acoes_realizadas: null,
	padrao_trabalho: null,
	observacoes: '',
	classificacao_final: '',
	sub_classificacao_final: '',
	tipo_incidente_final: '',
	tipo_incidente_final_outros: '',
};

const avoidPageBreakStyle = {
	breakInside: 'avoid',
	pageBreakInside: 'avoid',
	WebkitColumnBreakInside: 'avoid',
};

const formatBooleanLabel = (value) => {
	if (value === true) return 'Sim';
	if (value === false) return 'Não';
	return '';
};

const formatClassificationLabel = (value) => {
	if (!value) return '';
	const noSeparators = String(value).replace(/[_-]+/g, ' ');
	const lower = noSeparators.toLowerCase();
	return lower.charAt(0).toUpperCase() + lower.slice(1);
};

const normalizeBoolean = (value) => {
	if (value === true || value === 'sim') return true;
	if (value === false || value === 'nao') return false;
	return null;
};

const buildAvaliacaoData = (avaliacao, choices) => {
	if (!avaliacao) return emptyAvaliacaoData;

	let neverEventLabel = avaliacao.sub_classificacao_final || '';
	const subChoices = choices?.sub_classificacao || {};
	if (avaliacao.sub_classificacao_final && subChoices) {
		const entry = Object.entries(subChoices).find(
			([key, item]) =>
				String(key) === String(avaliacao.sub_classificacao_final) ||
				item?.codigo === avaliacao.sub_classificacao_final,
		);
		if (entry?.[1]?.nome) {
			neverEventLabel = entry[1].nome;
		}
	}

	return {
		acoes_realizadas: normalizeBoolean(avaliacao.acoes_realizadas),
		padrao_trabalho: normalizeBoolean(avaliacao.padrao_trabalho),
		observacoes: avaliacao.observacoes || '',
		classificacao_final: formatClassificationLabel(
			avaliacao.classificacao_final,
		),
		sub_classificacao_final: neverEventLabel,
		tipo_incidente_final:
			choices?.tipo_incidente?.[avaliacao.tipo_incidente_final] ||
			avaliacao.tipo_incidente_final ||
			'',
		tipo_incidente_final_outros:
			avaliacao.tipo_incidente_final_outros || '',
	};
};

const PdfField = ({ label, value, className = '' }) => (
	<div className={`flex min-w-0 w-full flex-col gap-1 ${className}`}>
		<p className='w-full px-2 text-left text-[#004A80] font-medium text-[13.5px] leading-[16px]'>
			{label}
		</p>
		<div className='min-h-[28px] w-full overflow-hidden px-2 py-1 text-left whitespace-pre-wrap break-all text-[11px] leading-[14px]'>
			{value || 'não informado'}
		</div>
	</div>
);

const avaliacaoListInFlight = new Map();
const inputsChoicesInFlight = new Map();

const getRequestCacheKey = (...parts) =>
	parts.map((part) => String(part || '').trim()).join('|');

const requestAvalListOnce = (token) => {
	const key = getRequestCacheKey('avaliacoes', token);
	if (avaliacaoListInFlight.has(key)) {
		return avaliacaoListInFlight.get(key);
	}

	const requestPromise = requestAvalList(token).finally(() => {
		avaliacaoListInFlight.delete(key);
	});
	avaliacaoListInFlight.set(key, requestPromise);
	return requestPromise;
};

const requestInputsChoicesOnce = () => {
	const key = 'inputs-choices';
	if (inputsChoicesInFlight.has(key)) {
		return inputsChoicesInFlight.get(key);
	}

	const requestPromise = requestInputsChoices().finally(() => {
		inputsChoicesInFlight.delete(key);
	});
	inputsChoicesInFlight.set(key, requestPromise);
	return requestPromise;
};

const loadAvaliacaoData = async (notificacao, avaliacaoOverride = null) => {
	if (!notificacao?.protocolo) return emptyAvaliacaoData;

	const userCookie = Cookies.get('user');
	if (!userCookie) return emptyAvaliacaoData;

	const { token } = JSON.parse(userCookie);
	if (!token) return emptyAvaliacaoData;

	const choices = await requestInputsChoicesOnce().catch(() => null);

	if (avaliacaoOverride) {
		return buildAvaliacaoData(avaliacaoOverride, choices);
	}

	const avaliacoes = await requestAvalListOnce(token);

	if (!Array.isArray(avaliacoes) || avaliacoes.length === 0) {
		return emptyAvaliacaoData;
	}

	const avaliacao = avaliacoes.find(
		(item) => item?.protocolo === notificacao.protocolo,
	);

	if (!avaliacao) return emptyAvaliacaoData;

	return buildAvaliacaoData(avaliacao, choices);
};

const NewAvaliacaoPDF = forwardRef(
	(
		{
			notificacao,
			hideAvaliacao = false,
			hideInvestigacao = false,
			classificacao = null,
			avaliacao = null,
			onReady,
		},
		ref,
	) => {
		const [formData, setFormData] = useState(emptyAvaliacaoData);
		const [notificacaoReady, setNotificacaoReady] = useState(false);
		const [classificacaoReady, setClassificacaoReady] =
			useState(hideInvestigacao);
		const [investigacaoReady, setInvestigacaoReady] =
			useState(hideInvestigacao);
		const [avaliacaoReady, setAvaliacaoReady] = useState(hideAvaliacao);
		const protocolo = notificacao?.protocolo;
		const loadedAvaliacaoKeyRef = React.useRef('');

		useEffect(() => {
			if (
				notificacaoReady &&
				(hideInvestigacao || classificacaoReady) &&
				(hideInvestigacao || investigacaoReady) &&
				(hideAvaliacao || avaliacaoReady)
			) {
				onReady?.();
			}
		}, [
			notificacaoReady,
			classificacaoReady,
			investigacaoReady,
			avaliacaoReady,
			hideInvestigacao,
			hideAvaliacao,
			onReady,
		]);

		useEffect(() => {
			if (hideAvaliacao) {
				setFormData(emptyAvaliacaoData);
				loadedAvaliacaoKeyRef.current = '';
				setAvaliacaoReady(true);
				return;
			}

			const handleLoad = async () => {
				try {
					setAvaliacaoReady(false);
					if (!protocolo) {
						setAvaliacaoReady(true);
						return;
					}
					const userCookie = Cookies.get('user');
					const token = userCookie
						? JSON.parse(userCookie)?.token
						: '';
					const loadKey = `${token}|${protocolo}|${JSON.stringify(
						avaliacao || {},
					)}`;
					if (loadedAvaliacaoKeyRef.current === loadKey) {
						setAvaliacaoReady(true);
						return;
					}
					loadedAvaliacaoKeyRef.current = loadKey;

					const result = await loadAvaliacaoData(
						{ protocolo },
						avaliacao,
					);
					setFormData(result);
					setAvaliacaoReady(true);
				} catch (error) {
					loadedAvaliacaoKeyRef.current = '';
					console.error(
						'Erro ao carregar avaliação final para PDF:',
						error,
					);
					setAvaliacaoReady(true);
				}
			};

			handleLoad();
		}, [protocolo, hideAvaliacao, avaliacao]);

		const hasReclassificacao = Boolean(
			formData.classificacao_final ||
			formData.tipo_incidente_final ||
			formData.tipo_incidente_final_outros,
		);
		const justificativaDescarte = String(
			classificacao?.justificativa_descarte || '',
		).trim();
		const encerradoNaClassificacao =
			hideInvestigacao &&
			(classificacao?.descartar === true ||
				Boolean(justificativaDescarte));

		return (
			<div
				ref={ref}
				className='w-[800px] bg-white font-inter'>
				{/* Etapas anteriores */}
				<NewNotificacaoPDF
					notificacao={notificacao}
					onReady={() => setNotificacaoReady(true)}
				/>
				{encerradoNaClassificacao && (
					<div className='p-[16px] break-inside-avoid page-break-inside-avoid'>
						<section
							data-pdf-block='true'
							className='border border-[#E3E3E3] rounded-md'
							style={avoidPageBreakStyle}>
							<div className='text-[#004A80] bg-[#F4F4F4] rounded-t-md text-[18px] text-center font-semibold border-[#E3E3E3] border-b-[1px] py-1 pb-6'>
								<h2>ENCERRAMENTO NA CLASSIFICACAO</h2>
							</div>
							<div className='p-3 flex flex-col gap-3'>
								<p className='w-full px-2 text-left text-[#004A80] font-medium text-[13.5px] leading-[16px]'>
									Esta notificacao foi concluida na etapa de
									classificacao, baseada na justificativa
									abaixo:
								</p>
								<div className='min-h-[80px] w-full overflow-hidden border border-[#E3E3E3] rounded-md px-2 py-2 text-left whitespace-pre-wrap break-all text-[11px] leading-[14px]'>
									{justificativaDescarte || 'nao informado'}
								</div>
							</div>
						</section>
					</div>
				)}
				{!hideInvestigacao && (
					<NewClassificacaoPDF
						notificacao={notificacao}
						classificacao={classificacao}
						onReady={() => setClassificacaoReady(true)}
					/>
				)}
				{!hideInvestigacao && (
					<NewInvestigacaoPDF
						notificacao={notificacao}
						onReady={() => setInvestigacaoReady(true)}
					/>
				)}

				{/* Avaliação Final */}
				{!hideAvaliacao && (
					<div className='p-[16px] break-inside-avoid page-break-inside-avoid'>
						<div className='flex flex-col gap-[16px] mt-[16px]'>
							<section
								data-pdf-block='true'
								className='border border-[#E3E3E3] rounded-md'
								style={avoidPageBreakStyle}>
								<div className='text-[#004A80] bg-[#F4F4F4] rounded-t-md text-[18px] text-center font-semibold border-[#E3E3E3] border-b-[1px] py-1 pb-6'>
									<h2>AVALIAÇÃO FINAL</h2>
								</div>
								<div className='p-3 grid grid-cols-2 gap-3'>
									<PdfField
										label='As ações realizadas foram suficientes para bloquear a reincidência do incidente?'
										value={formatBooleanLabel(
											formData.acoes_realizadas,
										)}
									/>
									<PdfField
										label='Foi criado ou alterado algum padrão de trabalho para evitar a reincidência do incidente?'
										value={formatBooleanLabel(
											formData.padrao_trabalho,
										)}
									/>
									<PdfField
										label='Observações'
										value={formData.observacoes}
										className='col-span-2'
									/>
								</div>
							</section>

							{hasReclassificacao && (
								<section
									data-pdf-block='true'
									className='border border-[#E3E3E3] rounded-md'
									style={avoidPageBreakStyle}>
									<div className='text-[#004A80] bg-[#F4F4F4] rounded-t-md text-[18px] text-center font-semibold border-[#E3E3E3] border-b-[1px] py-1 pb-6'>
										<h2>CLASSIFICAÇÃO CORRIGIDA</h2>
									</div>
									<div className='p-3 grid grid-cols-2 gap-3'>
										<PdfField
											label='Nova classificação'
											value={formData.classificacao_final}
										/>
										{formData.classificacao_final.toUpperCase() ===
											'Never events'.toUpperCase() && (
											<PdfField
												label='Never Event'
												value={
													formData.sub_classificacao_final
												}
											/>
										)}
										<PdfField
											label='Novo tipo de incidente'
											value={
												formData.tipo_incidente_final
											}
										/>
										{formData.tipo_incidente_final ===
											'OUTROS' && (
											<PdfField
												label='Outro tipo de incidente'
												value={
													formData.tipo_incidente_final_outros
												}
											/>
										)}
									</div>
								</section>
							)}
						</div>
					</div>
				)}
			</div>
		);
	},
);

export default NewAvaliacaoPDF;
